import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { showErrorToast } from '@/utils/errorHandler';
import { copyToClipboard as copyToClipboardUtil } from '@/utils/clipboard';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Ship,
  Anchor,
  Battery,
  Navigation,
  Thermometer,
  Gauge,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Check,
  Bell,
  Settings2,
  Waves,
  Wind,
  Compass,
  Clock,
  Fuel,
  Activity,
  MapPin,
  FileText,
  Sparkles,
  Loader2,
  Truck,
  Car,
  RefreshCw,
  Trash2,
  Plus,
  HelpCircle,
  Link2,
  AlertTriangle,
  Copy,
  Terminal,
  Download,
  Wifi,
  WifiOff,
  QrCode,
  Cloud,
  Cpu,
  Key,
} from 'lucide-react';
import { DredgeIcon, WheelLoaderIcon, EngineIcon, TugboatIcon } from '@/components/icons/CustomIcons';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = process.env.REACT_APP_BACKEND_URL;

// Custom marker icon for fixed location
const fixedLocationIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Map click handler component
const LocationSelector = ({ position, onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });

  return position ? (
    <Marker position={position} icon={fixedLocationIcon} />
  ) : null;
};

// Mapeamento de ícones por valor do campo 'icon' do backend
const EQUIPMENT_ICON_MAP = {
  ship: TugboatIcon,
  dredge: DredgeIcon,
  car: Car,
  truck: Truck,
  loader: WheelLoaderIcon,
  engine: EngineIcon,
  settings: Settings2,
  cloud: Cloud,
};

// Fallback: mapeamento por nome (case-insensitive, sem acentos)
const EQUIPMENT_TYPE_ICONS = {
  rebocador: TugboatIcon,
  draga: DredgeIcon,
  'pa carregadeira': WheelLoaderIcon,
  'pá carregadeira': WheelLoaderIcon,
  caminhao: Truck,
  'caminhão': Truck,
  'veiculo leve': Car,
  'veículo leve': Car,
  'motor estacionario': EngineIcon,
  'motor estacionário': EngineIcon,
  virtual: Cloud,
};

// Função helper para obter ícone do tipo de equipamento
const getEquipmentTypeIcon = (type) => {
  // Primeiro tenta pelo campo 'icon' do backend
  if (type.icon && EQUIPMENT_ICON_MAP[type.icon]) {
    return EQUIPMENT_ICON_MAP[type.icon];
  }
  // Fallback pelo nome
  const nameKey = type.name?.toLowerCase();
  if (nameKey && EQUIPMENT_TYPE_ICONS[nameKey]) {
    return EQUIPMENT_TYPE_ICONS[nameKey];
  }
  // Default
  return HelpCircle;
};

// Step configurations - Fase 3 Redesign: Hardware Provisioning ONLY
// Sensor configuration is a SEPARATE STEP (Etapa 2) after hardware_ready
const WIZARD_STEPS = [
  { id: 'zero-touch', title: 'Provisionamento', icon: QrCode, description: 'Gerar código e instalar' },
  { id: 'identification', title: 'Identificação', icon: FileText, description: 'Dados básicos' },
  { id: 'type-profile', title: 'Tipo', icon: Ship, description: 'Selecione o tipo' },
  { id: 'location', title: 'Localização', icon: MapPin, description: 'GPS ou fixa' },
  { id: 'review', title: 'Confirmação', icon: Check, description: 'Revise e confirme' },
];

// Default zones
const DEFAULT_ZONES = {
  rpm: [
    { min: 0, max: 600, color: 'blue', label: 'Marcha Lenta' },
    { min: 600, max: 2500, color: 'green', label: 'Operação Normal' },
    { min: 2500, max: 2800, color: 'yellow', label: 'Atenção' },
    { min: 2800, max: 3500, color: 'red', label: 'Crítico' },
  ],
  temperature: [
    { min: 0, max: 60, color: 'blue', label: 'Frio' },
    { min: 60, max: 85, color: 'green', label: 'Normal' },
    { min: 85, max: 95, color: 'yellow', label: 'Atenção' },
    { min: 95, max: 120, color: 'red', label: 'Superaquecimento' },
  ],
  voltage: [
    { min: 0, max: 11, color: 'red', label: 'Crítico Baixo' },
    { min: 11, max: 12, color: 'yellow', label: 'Baixo' },
    { min: 12, max: 14.5, color: 'green', label: 'Normal' },
    { min: 14.5, max: 15, color: 'yellow', label: 'Alto' },
    { min: 15, max: 20, color: 'red', label: 'Crítico Alto' },
  ],
  oil_pressure: [
    { min: 0, max: 1.5, color: 'red', label: 'Crítico Baixo' },
    { min: 1.5, max: 2.5, color: 'yellow', label: 'Baixo' },
    { min: 2.5, max: 6, color: 'green', label: 'Normal' },
    { min: 6, max: 8, color: 'yellow', label: 'Alto' },
    { min: 8, max: 10, color: 'red', label: 'Crítico Alto' },
  ],
  depth: [
    { min: 0, max: 5, color: 'red', label: 'Raso - Perigo' },
    { min: 5, max: 10, color: 'yellow', label: 'Raso - Atenção' },
    { min: 10, max: 500, color: 'green', label: 'Seguro' },
  ],
};

// Source Selector using REAL SignalK paths
const SourceSelector = ({ value, onChange, signalkPaths = [], category = 'all' }) => {
  // Filter paths by category if specified
  const filteredPaths = category === 'all' 
    ? signalkPaths 
    : signalkPaths.filter(p => p.category === category);
  
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground flex items-center gap-1">
        <Link2 className="w-3 h-3" />
        Source Path SignalK
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-xs font-mono">
          <SelectValue placeholder="Selecione o source SignalK" />
        </SelectTrigger>
        <SelectContent>
          {filteredPaths.map((source, idx) => (
            <SelectItem key={idx} value={source.path} className="text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono">{source.path}</span>
                <span className="text-muted-foreground">({source.label})</span>
              </div>
            </SelectItem>
          ))}
          {filteredPaths.length === 0 && (
            <SelectItem value="" disabled className="text-xs text-muted-foreground">
              Nenhum source disponível para esta categoria
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </div>
  );
};

// Alert Configuration Component
const AlertConfig = ({ config, onChange, zones, onZonesChange }) => {
  const [showZones, setShowZones] = useState(false);
  
  return (
    <div className="space-y-2 p-3 rounded-lg border bg-muted/30">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={config?.enabled}
            onCheckedChange={(checked) => onChange({ ...config, enabled: checked })}
          />
          <Bell className="w-4 h-4 text-yellow-500" />
          <span className="text-sm font-medium">Alertas</span>
        </div>
        {config?.enabled && zones && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowZones(!showZones)}
            className="text-xs"
          >
            Configurar faixas
            <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showZones ? 'rotate-180' : ''}`} />
          </Button>
        )}
      </div>
      
      {config?.enabled && showZones && zones && (
        <div className="space-y-2 pt-2 border-t">
          <Label className="text-xs text-muted-foreground">Zonas de Operação</Label>
          {zones.map((zone, idx) => (
            <div key={idx} className="flex items-center gap-2 text-xs">
              <div 
                className={`w-3 h-3 rounded-full flex-shrink-0 ${
                  zone.color === 'green' ? 'bg-green-500' :
                  zone.color === 'yellow' ? 'bg-yellow-500' :
                  zone.color === 'red' ? 'bg-red-500' :
                  zone.color === 'blue' ? 'bg-blue-500' :
                  'bg-gray-500'
                }`}
              />
              <Input
                type="number"
                value={zone.min}
                onChange={(e) => {
                  const newZones = [...zones];
                  newZones[idx] = { ...zone, min: parseFloat(e.target.value) || 0 };
                  onZonesChange(newZones);
                }}
                className="w-16 h-7 text-xs"
              />
              <span className="text-muted-foreground">a</span>
              <Input
                type="number"
                value={zone.max}
                onChange={(e) => {
                  const newZones = [...zones];
                  newZones[idx] = { ...zone, max: parseFloat(e.target.value) || 0 };
                  onZonesChange(newZones);
                }}
                className="w-16 h-7 text-xs"
              />
              <span className="text-xs text-muted-foreground flex-1">{zone.label}</span>
            </div>
          ))}
          
          <div className="flex items-center gap-2 pt-2">
            <Label className="text-xs">Tempo mínimo (seg):</Label>
            <Input
              type="number"
              value={config?.minDuration || 5}
              onChange={(e) => onChange({ ...config, minDuration: parseInt(e.target.value) || 5 })}
              className="w-16 h-7 text-xs"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Sensor Configuration Card
const SensorCard = ({ 
  sensorKey,
  config, 
  onChange,
  onDelete,
  signalkPaths,
  category,
  icon: Icon,
  label,
  defaultZones,
  canDelete = true,
}) => {
  const [expanded, setExpanded] = useState(config?.enabled);
  
  return (
    <Card className={`mb-2 ${!config?.enabled ? 'opacity-60' : ''}`}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={config?.enabled}
              onCheckedChange={(checked) => {
                onChange({ 
                  ...config, 
                  enabled: checked,
                  zones: config?.zones || defaultZones,
                  alert: config?.alert || { enabled: false, minDuration: 5 },
                });
                setExpanded(checked);
              }}
            />
            <Icon className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium text-sm">{label}</span>
          </div>
          <div className="flex items-center gap-2">
            {canDelete && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onDelete}
                className="h-7 w-7 text-destructive hover:text-destructive"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            {config?.enabled && (
              <ChevronDown 
                className={`w-4 h-4 cursor-pointer transition-transform ${expanded ? 'rotate-180' : ''}`}
                onClick={() => setExpanded(!expanded)}
              />
            )}
          </div>
        </div>
        
        {config?.enabled && expanded && (
          <div className="pl-6 space-y-3 pt-2 border-t">
            <SourceSelector
              value={config?.source}
              onChange={(source) => onChange({ ...config, source })}
              signalkPaths={signalkPaths}
              category={category}
            />
            
            {defaultZones && (
              <AlertConfig
                config={config?.alert || { enabled: false }}
                onChange={(alert) => onChange({ ...config, alert })}
                zones={config?.zones || defaultZones}
                onZonesChange={(zones) => onChange({ ...config, zones })}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Provisioning Wizard Component
const ProvisioningWizard = ({ 
  open, 
  onClose, 
  existingEquipment = null,
  onComplete 
}) => {
  const navigate = useNavigate();
  const { token } = useAuth(); // Usar token do AuthContext
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showProceedWithoutConnection, setShowProceedWithoutConnection] = useState(false);
  
  // Zero-Touch Provisioning state
  const [generating, setGenerating] = useState(false);
  const [provisioningData, setProvisioningData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [deviceConnected, setDeviceConnected] = useState(false);
  const [isVirtualEquipment, setIsVirtualEquipment] = useState(false);
  const [virtualCredentials, setVirtualCredentials] = useState(null);
  const [proceedWithoutConnection, setProceedWithoutConnection] = useState(false);
  
  // Data from backend
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [signalkPaths, setSignalkPaths] = useState([]);
  const [displays, setDisplays] = useState([]);
  
  // Configuration state
  const [config, setConfig] = useState({
    identification: {
      name: '',
      tag_senior: '',
      location_type: 'gps',
      latitude: null,
      longitude: null,
      description: '',
    },
    typeProfile: {
      equipment_type_id: '',
    },
    propulsion: {
      sensors: [],
    },
    electrical: {
      sensors: [],
    },
    navigation: {
      sensors: [],
    },
  });
  
  // Reset wizard state to initial values
  const resetWizardState = useCallback(() => {
    setCurrentStep(0);
    setGenerating(false);
    setProvisioningData(null);
    setCopied(false);
    setDeviceConnected(false);
    setIsVirtualEquipment(false);
    setVirtualCredentials(null);
    setConfig({
      identification: {
        name: '',
        tag_senior: '',
        location_type: 'gps',
        latitude: null,
        longitude: null,
        description: '',
      },
      typeProfile: {
        equipment_type_id: '',
      },
      propulsion: {
        sensors: [],
      },
      electrical: {
        sensors: [],
      },
      navigation: {
        sensors: [],
      },
    });
  }, []);

  // Load initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const [typesRes, pathsRes, displaysRes] = await Promise.all([
          axios.get(`${API}/api/equipment-types`, { headers }),
          axios.get(`${API}/api/signalk-paths`, { headers }),
          axios.get(`${API}/api/displays`, { headers }),
        ]);
        
        // Use equipment types from backend - no dynamic "Outro" type
        const types = typesRes.data || [];
        
        setEquipmentTypes(types);
        setSignalkPaths(pathsRes.data || []);
        setDisplays(displaysRes.data || []);
        
        // Se é reprovisionamento, carregar config existente.
        // O passo zero-touch NÃO é pulado: é nele que se gera o código
        // vinculado ao equipamento para revincular o hardware ao RPi.
        if (existingEquipment) {
          loadExistingConfig(existingEquipment);
        }
        // Novo equipamento: NÃO inicializar sensores automaticamente
        // Sensores são configurados em etapa separada (Etapa 2 do PSD)
      } catch (error) {
        console.error('Error fetching wizard data:', error);
        toast.error('Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };
    
    if (open) {
      // SEMPRE resetar estado ao abrir wizard para novo equipamento
      if (!existingEquipment) {
        resetWizardState();
      }
      fetchData();
    }
  }, [open, existingEquipment, resetWizardState, token]);
  
  // Polling to check if device connected (after code is generated)
  useEffect(() => {
    // Only poll if:
    // 1. We have provisioning data with claim_code (newly generated)
    // 2. Device is not already connected
    // 3. It's not a virtual equipment
    if (!provisioningData?.claim_code || deviceConnected || isVirtualEquipment) {
      return;
    }
    
    const checkDeviceStatus = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        // Bug #2 Fix: Usar /provisioning/status em vez de /equipments
        // Agora o equipamento só existe após o wizard ser completado
        const response = await axios.get(
          `${API}/api/provisioning/status/${provisioningData.claim_code}`,
          { headers }
        );
        
        // device_connected é true quando o dispositivo executou o claim.
        // Reprovisionamento: um equipamento já provisionado antes pode reportar
        // device_connected=true por estado antigo (hardware_ready); a prova de
        // vinculação é o resgate DESTE token (status 'redeemed' no /claim).
        const connected = existingEquipment
          ? response.data.status === 'redeemed'
          : response.data.device_connected;
        if (connected) {
          setDeviceConnected(true);
          toast.success('Device conectado com sucesso!');
        }
      } catch (error) {
        // Silent fail - device not ready yet
        console.debug('Polling device status:', error.message);
      }
    };
    
    // Poll every 5 seconds
    const interval = setInterval(checkDeviceStatus, 5000);
    
    // First check after 10 seconds - RPi needs time to:
    // 1. Download the agent script
    // 2. Install dependencies
    // 3. Execute claim
    const timeout = setTimeout(checkDeviceStatus, 10000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [provisioningData?.claim_code, deviceConnected, isVirtualEquipment, token, existingEquipment]);
  
  const loadExistingConfig = (equipment) => {
    setConfig(prev => ({
      ...prev,
      identification: {
        name: equipment.name || '',
        tag_senior: equipment.tag_senior || '',
        location_type: equipment.location_type || 'gps',
        latitude: equipment.latitude,
        longitude: equipment.longitude,
        description: equipment.description || '',
      },
      typeProfile: {
        equipment_type_id: equipment.equipment_type_id || '',
      }
    }));
  };
  
  const initializeDefaultSensors = (paths) => {
    // Propulsion sensors from SignalK
    const propulsionPaths = paths.filter(p => p.category === 'propulsion');
    const propulsionSensors = propulsionPaths.map(p => ({
      id: `sensor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      label: p.label,
      source: p.path,
      enabled: true,
      category: 'propulsion',
      zones: p.path.includes('revolutions') ? DEFAULT_ZONES.rpm :
             p.path.includes('temperature') ? DEFAULT_ZONES.temperature :
             p.path.includes('oilPressure') ? DEFAULT_ZONES.oil_pressure : null,
      alert: { enabled: true, minDuration: 5 },
    }));
    
    // Electrical sensors
    const electricalPaths = paths.filter(p => p.category === 'electrical');
    const electricalSensors = electricalPaths.map(p => ({
      id: `sensor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      label: p.label,
      source: p.path,
      enabled: true,
      category: 'electrical',
      zones: DEFAULT_ZONES.voltage,
      alert: { enabled: true, minDuration: 5 },
    }));
    
    // Navigation sensors
    const navigationPaths = paths.filter(p => p.category === 'navigation');
    const navigationSensors = navigationPaths.map(p => ({
      id: `sensor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      label: p.label,
      source: p.path,
      enabled: p.path.includes('position') || p.path.includes('speed'),
      category: 'navigation',
      zones: null,
      alert: { enabled: false },
    }));
    
    setConfig(prev => ({
      ...prev,
      propulsion: { sensors: propulsionSensors },
      electrical: { sensors: electricalSensors },
      navigation: { sensors: navigationSensors },
    }));
  };
  
  // Generate Zero-Touch Provisioning Code
  // Bug #2 Fix: NÃO cria equipamento aqui, apenas gera claim code
  const generateProvisioningCode = async () => {
    if (!existingEquipment && !config.identification.name) {
      toast.error('Informe o nome do equipamento primeiro');
      return;
    }

    setGenerating(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      let response;
      if (existingEquipment) {
        // REPROVISIONAMENTO: código vinculado ao equipamento existente.
        // O backend expira tokens pendentes anteriores deste equipamento e o
        // /claim revincula o hardware (fingerprint) a ele, desvinculando o
        // device de qualquer outro equipamento (1 dispositivo = 1 equipamento).
        response = await axios.post(`${API}/api/provisioning/tokens`, {
          equipment_id: existingEquipment.id,
          expires_in_hours: 24
        }, { headers });
      } else {
        // APENAS gerar código de provisionamento (NÃO cria equipamento)
        // O equipamento será criado apenas no final do wizard via /api/provisioning/complete
        response = await axios.post(`${API}/api/provisioning/generate-claim-code`, {
          equipment_name: config.identification.name,
          expires_in_hours: 24
        }, { headers });
      }

      setProvisioningData({
        claim_code: response.data.claim_code,
        equipment_name: response.data.equipment_name,
        expires_at: response.data.expires_at,
      });

      toast.success('Código de provisionamento gerado!');
    } catch (error) {
      console.error('Error generating code:', error);
      showErrorToast(toast, error, 'Erro ao gerar código');
    } finally {
      setGenerating(false);
    }
  };
  
  // Create Virtual Equipment
  const createVirtualEquipment = async () => {
    if (!config.identification.name) {
      toast.error('Informe o nome do equipamento primeiro');
      return;
    }
    
    setGenerating(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Find the "Virtual" equipment type
      const virtualType = equipmentTypes.find(t => t.name.toLowerCase() === 'virtual');
      
      const response = await axios.post(`${API}/api/provisioning/virtual`, {
        name: config.identification.name,
        equipment_type_id: virtualType?.id || null,
        description: config.identification.description || '',
      }, { headers });
      
      setVirtualCredentials(response.data);
      setDeviceConnected(true); // Virtual equipment is always "connected"
      
      // Store the equipment_id from response and set the type to Virtual
      setProvisioningData({
        equipment_id: response.data.equipment.id,
      });
      
      // Auto-set the type profile to Virtual
      if (virtualType) {
        setConfig(prev => ({
          ...prev,
          typeProfile: { equipment_type_id: virtualType.id }
        }));
      }
      
      toast.success(`Equipamento virtual "${response.data.equipment.name}" criado!`);
    } catch (error) {
      console.error('Error creating virtual equipment:', error);
      showErrorToast(toast, error, 'Erro ao criar equipamento virtual');
    } finally {
      setGenerating(false);
    }
  };
  
  // Função de cópia usando o utilitário centralizado
  const copyToClipboard = async (text, successMessage = 'Copiado!') => {
    const success = await copyToClipboardUtil(text);
    if (success) {
      toast.success(successMessage);
    } else {
      toast.info('Selecione o texto e use Ctrl+C para copiar');
    }
    return success;
  };
  
  const copyCommand = async () => {
    if (!provisioningData) return;
    
    // Usar API_BASE que aponta para o backend (api.octopus.areia.com.br)
    const API_BASE = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const command = `curl -sSL "${API_BASE}/api/provisioning/download-agent?code=${provisioningData.claim_code}" | sudo python3 -`;
    const success = await copyToClipboard(command, 'Comando copiado!');
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };
  
  const handleNext = () => {
    // Validações por etapa (novo fluxo Fase 3 - Hardware Provisioning)
    // Steps: 0=zero-touch, 1=identification, 2=type-profile, 3=location, 4=review
    
    if (currentStep === 0 && !existingEquipment) {
      // Zero-Touch step - need code generated, virtual created, or skip
      if (!provisioningData && !deviceConnected && !isVirtualEquipment) {
        toast.info('Gere o código de provisionamento, crie equipamento virtual, ou clique em "Pular"');
        return;
      }
      // Virtual equipment: if already created, skip identification and type (already set)
      if (isVirtualEquipment && virtualCredentials) {
        // Skip to location step (step 3)
        setCurrentStep(3);
        return;
      }
    }
    if (currentStep === 1) {
      if (!config.identification.name.trim()) {
        toast.error('Informe o nome do equipamento');
        return;
      }
    }
    if (currentStep === 2) {
      // Virtual equipment already has type set, skip validation
      if (isVirtualEquipment) {
        // Type already set when virtual equipment was created
      } else if (!config.typeProfile.equipment_type_id) {
        toast.error('Selecione o tipo de equipamento');
        return;
      }
    }
    if (currentStep === 3) {
      // Location step validation - Virtual equipment MUST have fixed location
      if (isVirtualEquipment || config.identification.location_type === 'fixed') {
        if (!config.identification.latitude || !config.identification.longitude) {
          toast.error('Informe as coordenadas de localização fixa');
          return;
        }
      }
    }
    
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const skipZeroTouch = () => {
    setDeviceConnected(true);
    setCurrentStep(1);
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      let equipmentId;
      
      // Bug #2 Fix: Fluxo diferente para novo equipamento vs reprovisionamento
      if (existingEquipment) {
        // REPROVISIONAMENTO: equipamento já existe, apenas atualizar
        equipmentId = existingEquipment.id;
        
        const equipmentData = {
          name: config.identification.name,
          equipment_type_id: config.typeProfile.equipment_type_id || undefined,
          location_type: config.identification.location_type,
          latitude: config.identification.latitude,
          longitude: config.identification.longitude,
          tag_senior: config.identification.tag_senior || undefined,
          description: config.identification.description || undefined,
          wizard_configured: true,
        };
        
        // Remove undefined values
        Object.keys(equipmentData).forEach(key => 
          equipmentData[key] === undefined && delete equipmentData[key]
        );
        
        await axios.put(`${API}/api/equipments/${equipmentId}`, equipmentData, { headers });
        
      } else if (isVirtualEquipment && virtualCredentials) {
        // EQUIPAMENTO VIRTUAL: já foi criado via /provisioning/virtual
        equipmentId = virtualCredentials.equipment.id;
        
        // Atualizar com dados finais
        const equipmentData = {
          name: config.identification.name,
          location_type: 'fixed',
          latitude: config.identification.latitude,
          longitude: config.identification.longitude,
          description: config.identification.description || undefined,
          wizard_configured: true,
        };
        
        Object.keys(equipmentData).forEach(key => 
          equipmentData[key] === undefined && delete equipmentData[key]
        );
        
        await axios.put(`${API}/api/equipments/${equipmentId}`, equipmentData, { headers });
        
      } else if (provisioningData?.claim_code) {
        // NOVO EQUIPAMENTO via Zero-Touch: usar /provisioning/complete
        // Bug #2 Fix: Equipamento só é criado AQUI, no final do wizard
        const response = await axios.post(`${API}/api/provisioning/complete`, {
          claim_code: provisioningData.claim_code,
          name: config.identification.name,
          equipment_type_id: config.typeProfile.equipment_type_id || null,
          location_type: config.identification.location_type,
          latitude: config.identification.latitude,
          longitude: config.identification.longitude,
          tag_senior: config.identification.tag_senior || null,
          description: config.identification.description || null,
          proceed_without_connection: proceedWithoutConnection,
        }, { headers });
        
        equipmentId = response.data.equipment_id;
        
        // Mostrar mensagem apropriada baseada no status
        if (response.data.status === 'awaiting_connection') {
          toast.info('Equipamento criado. Aguardando primeira conexão do dispositivo.');
        }
        
      } else {
        // Fallback: criar equipamento diretamente (fluxo "Pular")
        const equipmentData = {
          name: config.identification.name,
          equipment_type_id: config.typeProfile.equipment_type_id || undefined,
          location_type: config.identification.location_type,
          latitude: config.identification.latitude,
          longitude: config.identification.longitude,
          tag_senior: config.identification.tag_senior || undefined,
          description: config.identification.description || undefined,
        };
        
        Object.keys(equipmentData).forEach(key => 
          equipmentData[key] === undefined && delete equipmentData[key]
        );
        
        const createRes = await axios.post(`${API}/api/equipments`, equipmentData, { headers });
        equipmentId = createRes.data.id;
      }
      
      // Toast honesto: só afirmar reprovisionamento quando o hardware de fato
      // resgatou o código gerado neste wizard
      if (existingEquipment) {
        if (provisioningData?.claim_code && deviceConnected) {
          toast.success('Equipamento reprovisionado!');
        } else if (provisioningData?.claim_code) {
          toast.info('Cadastro atualizado. Execute o comando no RPi para concluir a vinculação do hardware (o código continua válido).');
        } else {
          toast.success('Cadastro do equipamento atualizado.');
        }
      } else {
        toast.success('Equipamento provisionado!');
      }
      onComplete?.(equipmentId, config);
      handleClose();
      navigate('/equipments');
      
    } catch (error) {
      console.error('Error saving:', error);
      showErrorToast(toast, error, 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };
  
  const updateSensor = (category, sensorId, newData) => {
    setConfig(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        sensors: prev[category].sensors.map(s => 
          s.id === sensorId ? { ...s, ...newData } : s
        ),
      },
    }));
  };
  
  const deleteSensor = (category, sensorId) => {
    setConfig(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        sensors: prev[category].sensors.filter(s => s.id !== sensorId),
      },
    }));
  };
  
  const addSensor = (category) => {
    const defaultSource = signalkPaths.find(p => p.category === category)?.path || '';
    
    setConfig(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        sensors: [
          ...prev[category].sensors,
          {
            id: `sensor_${Date.now()}`,
            label: 'Novo Sensor',
            source: defaultSource,
            enabled: true,
            category,
            zones: category === 'propulsion' ? DEFAULT_ZONES.rpm :
                   category === 'electrical' ? DEFAULT_ZONES.voltage : null,
            alert: { enabled: true, minDuration: 5 },
          },
        ],
      },
    }));
  };
  
  const getSensorIcon = (source) => {
    if (source.includes('revolutions')) return Gauge;
    if (source.includes('temperature')) return Thermometer;
    if (source.includes('oilPressure')) return Activity;
    if (source.includes('fuel')) return Fuel;
    if (source.includes('runTime')) return Clock;
    if (source.includes('voltage')) return Battery;
    if (source.includes('speed')) return Gauge;
    if (source.includes('course') || source.includes('heading')) return Compass;
    if (source.includes('position')) return MapPin;
    if (source.includes('depth')) return Waves;
    if (source.includes('wind')) return Wind;
    return Activity;
  };
  
  const renderStepContent = () => {
    const step = WIZARD_STEPS[currentStep];
    
    switch (step.id) {
      case 'zero-touch':
        return (
          <div className="space-y-4">
            {/* Reprovisionamento: sem escolha físico/virtual — o equipamento já existe */}
            {existingEquipment && (
              <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-sm space-y-1">
                <p>
                  <strong>Reprovisionamento de {existingEquipment.tag || existingEquipment.name}:</strong>{' '}
                  gere um novo código e execute o comando no RPi para vincular o hardware a este equipamento.
                </p>
                <p className="text-xs text-muted-foreground">
                  Gerar um código novo invalida códigos pendentes anteriores deste equipamento.
                  Se o RPi estava vinculado a outro equipamento, ele será desvinculado automaticamente.
                </p>
              </div>
            )}

            {/* Choice between Physical and Virtual */}
            {!existingEquipment && (
            <div className="grid grid-cols-2 gap-4">
              <Card 
                className={`cursor-pointer transition-all hover:border-primary ${!isVirtualEquipment ? 'border-primary bg-primary/5 ring-2 ring-primary' : ''}`}
                onClick={() => {
                  setIsVirtualEquipment(false);
                  setConfig(prev => ({
                    ...prev,
                    identification: { ...prev.identification, location_type: 'gps' }
                  }));
                }}
              >
                <CardContent className="p-4 text-center">
                  <Cpu className="w-10 h-10 mx-auto mb-2 text-blue-500" />
                  <p className="font-medium">Equipamento Físico</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Com Raspberry Pi e Octopus Agent
                  </p>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer transition-all hover:border-primary ${isVirtualEquipment ? 'border-primary bg-primary/5 ring-2 ring-primary' : ''}`}
                onClick={() => {
                  setIsVirtualEquipment(true);
                  // Virtual equipment MUST have fixed location
                  setConfig(prev => ({
                    ...prev,
                    identification: { ...prev.identification, location_type: 'fixed' }
                  }));
                }}
              >
                <CardContent className="p-4 text-center">
                  <Cloud className="w-10 h-10 mx-auto mb-2 text-purple-500" />
                  <p className="font-medium">Equipamento Virtual</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Sem hardware físico, via MQTT
                  </p>
                </CardContent>
              </Card>
            </div>
            )}

            {/* Equipment name */}
            <div className="space-y-2">
              <Label htmlFor="eq-name-zt">Nome do Equipamento *</Label>
              <Input
                id="eq-name-zt"
                value={config.identification.name}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  identification: { ...prev.identification, name: e.target.value }
                }))}
                placeholder={isVirtualEquipment ? "Ex: Motor Gerador Externo" : "Ex: Rebocador Areia I"}
                disabled={!!provisioningData || !!virtualCredentials}
              />
              {(provisioningData || virtualCredentials) && (
                <p className="text-xs text-muted-foreground">
                  Nome bloqueado após geração do código
                </p>
              )}
            </div>
            
            {/* Physical Equipment - Zero-Touch Flow (Redesigned) */}
            {!isVirtualEquipment && (
              <>
                {/* Gerar código - só aparece antes de gerar */}
                {!provisioningData && (
                  <Button
                    onClick={generateProvisioningCode}
                    disabled={generating || (!existingEquipment && !config.identification.name)}
                    className="w-full gap-2"
                  >
                    {generating ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <QrCode className="w-4 h-4" />
                    )}
                    {existingEquipment ? 'Gerar Código de Reprovisionamento' : 'Gerar Código de Provisionamento'}
                  </Button>
                )}
                
                {/* Layout em duas colunas após gerar código */}
                {provisioningData && provisioningData.claim_code && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Coluna Esquerda - Código e Comando */}
                    <div className="space-y-3">
                      {/* Código de 6 dígitos em DESTAQUE */}
                      <div className="p-4 rounded-lg bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/40">
                        <p className="text-xs text-muted-foreground text-center mb-1">CÓDIGO DE PROVISIONAMENTO</p>
                        <div className="flex items-center justify-center gap-2">
                          <div 
                            className="text-3xl md:text-4xl font-mono font-bold tracking-[0.3em] text-green-400 cursor-pointer hover:text-green-300 transition-colors"
                            onClick={() => copyToClipboard(provisioningData.claim_code, 'Código copiado!')}
                            title="Clique para copiar"
                          >
                            {provisioningData.claim_code}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-green-500 hover:text-green-400"
                            onClick={() => copyToClipboard(provisioningData.claim_code, 'Código copiado!')}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mt-2">
                          <Clock className="w-3 h-3" />
                          Expira em 24h
                        </div>
                      </div>
                      
                      {/* Comando curl compacto */}
                      <div className="p-3 rounded-lg bg-secondary/50 border space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium flex items-center gap-1">
                            <Terminal className="w-3 h-3" />
                            Comando para RPi
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={copyCommand}
                          >
                            {copied ? (
                              <><Check className="w-3 h-3 text-green-500 mr-1" />Copiado</>
                            ) : (
                              <><Copy className="w-3 h-3 mr-1" />Copiar</>
                            )}
                          </Button>
                        </div>
                        <code 
                          className="block text-[10px] bg-background/80 p-2 rounded font-mono break-all select-all cursor-pointer hover:bg-background leading-relaxed"
                          onClick={(e) => {
                            const range = document.createRange();
                            range.selectNodeContents(e.target);
                            const selection = window.getSelection();
                            selection.removeAllRanges();
                            selection.addRange(range);
                          }}
                        >
                          curl -sSL "{process.env.REACT_APP_BACKEND_URL || window.location.origin}/api/provisioning/download-agent?code={provisioningData.claim_code}" | sudo python3 -
                        </code>
                      </div>
                      
                      {/* Botão download alternativo - menor */}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="w-full text-xs h-7 text-muted-foreground"
                        onClick={() => {
                          const API_BASE = process.env.REACT_APP_BACKEND_URL || window.location.origin;
                          window.open(`${API_BASE}/api/provisioning/download-agent?code=${provisioningData.claim_code}`, '_blank');
                        }}
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Baixar agent manualmente
                      </Button>
                    </div>
                    
                    {/* Coluna Direita - Status de Conexão */}
                    <div className="flex flex-col justify-between">
                      {/* Status do dispositivo */}
                      <div className={cn(
                        "flex-1 rounded-lg border p-4 flex flex-col items-center justify-center text-center",
                        deviceConnected 
                          ? "bg-green-500/10 border-green-500/40" 
                          : "bg-secondary/30 border-border"
                      )}>
                        {deviceConnected ? (
                          <>
                            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mb-2">
                              <Check className="w-6 h-6 text-green-500" />
                            </div>
                            <p className="font-medium text-green-500">Dispositivo Conectado</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Pronto para continuar
                            </p>
                          </>
                        ) : (
                          <>
                            <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mb-2">
                              <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
                            </div>
                            <p className="font-medium text-muted-foreground">Aguardando Dispositivo</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Execute o comando no RPi
                            </p>
                          </>
                        )}
                      </div>
                      
                      {/* Botão Prosseguir mesmo assim - secundário */}
                      {!deviceConnected && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setShowProceedWithoutConnection(true)}
                          className="mt-3 w-full text-xs border-yellow-500/30 text-yellow-500 hover:bg-yellow-500/10 hover:text-yellow-400"
                        >
                          <WifiOff className="w-3 h-3 mr-1" />
                          Prosseguir sem conexão
                          <span className="text-muted-foreground ml-1">(aguardará depois)</span>
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Separador e botão pular - só antes de gerar código */}
                {!provisioningData && (
                  <>
                    <Separator />
                    <Button
                      variant="ghost"
                      onClick={skipZeroTouch}
                      className="w-full text-muted-foreground"
                    >
                      {existingEquipment ? 'Pular (apenas editar cadastro, sem revincular hardware)' : 'Pular (Configuração Manual)'}
                    </Button>
                  </>
                )}
              </>
            )}
            
            {/* Virtual Equipment - MQTT Flow */}
            {isVirtualEquipment && (
              <>
                {!virtualCredentials ? (
                  <>
                    <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                      <p className="text-sm">
                        <strong>Equipamento Virtual:</strong> Recebe dados via MQTT de fontes externas 
                        (scripts, integrações, etc.) sem necessidade de Raspberry Pi.
                      </p>
                    </div>
                    
                    <Button 
                      onClick={createVirtualEquipment}
                      disabled={generating || !config.identification.name}
                      className="w-full gap-2 bg-purple-600 hover:bg-purple-700"
                    >
                      {generating ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Cloud className="w-4 h-4" />
                      )}
                      Criar Equipamento Virtual
                    </Button>
                  </>
                ) : (
                  <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30 space-y-4">
                    <div className="text-center">
                      <Check className="w-10 h-10 mx-auto mb-2 text-green-500" />
                      <p className="font-medium">Equipamento Virtual Criado!</p>
                      <Badge className="mt-1">{virtualCredentials.equipment.tag}</Badge>
                    </div>
                    
                    <div className="border-t border-purple-500/30 pt-4 space-y-3">
                      <p className="text-sm font-medium flex items-center gap-2">
                        <Key className="w-4 h-4" />
                        Credenciais MQTT:
                      </p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Broker:</span>
                          <code className="bg-secondary px-2 py-0.5 rounded text-xs">
                            {virtualCredentials.mqtt_credentials.broker_host}:{virtualCredentials.mqtt_credentials.broker_port}
                          </code>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Tópico:</span>
                          <code className="bg-secondary px-2 py-0.5 rounded text-xs">
                            {virtualCredentials.mqtt_credentials.topics.telemetry}
                          </code>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
        
      case 'identification':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="eq-name">Nome do Equipamento *</Label>
              <Input
                id="eq-name"
                value={config.identification.name}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  identification: { ...prev.identification, name: e.target.value }
                }))}
                placeholder="Ex: Rebocador Areia I"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tag-senior">TAG SENIOR/SISMETRO</Label>
              <Input
                id="tag-senior"
                value={config.identification.tag_senior}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  identification: { ...prev.identification, tag_senior: e.target.value }
                }))}
                placeholder="Ex: REB001"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Input
                id="description"
                value={config.identification.description}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  identification: { ...prev.identification, description: e.target.value }
                }))}
                placeholder="Descrição adicional..."
              />
            </div>
          </div>
        );
        
      case 'type-profile':
        // If virtual equipment, show read-only info
        if (isVirtualEquipment) {
          const virtualType = equipmentTypes.find(t => t.name.toLowerCase() === 'virtual');
          const VirtualIcon = virtualType ? getEquipmentTypeIcon(virtualType) : Cloud;
          
          return (
            <div className="space-y-6">
              <div className="p-6 bg-purple-500/10 rounded-lg border border-purple-500/30 text-center">
                <VirtualIcon className="w-16 h-16 mx-auto mb-4 text-purple-500" />
                <h3 className="text-xl font-bold">Equipamento Virtual</h3>
                <p className="text-muted-foreground mt-2">
                  Equipamentos virtuais têm tipo automático "Virtual" e não podem ser alterados.
                </p>
              </div>
            </div>
          );
        }
        
        // Normal equipment type selection (excluding Virtual type)
        const physicalTypes = equipmentTypes.filter(t => t.name.toLowerCase() !== 'virtual');
        
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Tipo de Equipamento *</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {physicalTypes.map((type) => {
                  const Icon = getEquipmentTypeIcon(type);
                  const isSelected = config.typeProfile.equipment_type_id === type.id;
                  
                  return (
                    <Card
                      key={type.id}
                      className={`cursor-pointer transition-all hover:border-primary ${
                        isSelected ? 'border-primary bg-primary/5 ring-2 ring-primary' : ''
                      }`}
                      onClick={() => setConfig(prev => ({
                        ...prev,
                        typeProfile: { ...prev.typeProfile, equipment_type_id: type.id }
                      }))}
                    >
                      <CardContent className="p-4 text-center">
                        <Icon className={`w-8 h-8 mx-auto mb-2 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                        <p className={`font-medium text-sm ${isSelected ? 'text-primary' : ''}`}>{type.name}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        );
        
      case 'location':
        return (
          <div className="space-y-4">
            {/* Virtual equipment can ONLY have fixed location */}
            {isVirtualEquipment ? (
              <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                <div className="flex items-center gap-3">
                  <MapPin className="w-8 h-8 text-purple-500" />
                  <div>
                    <p className="font-medium">Equipamento Virtual - Localização Fixa</p>
                    <p className="text-sm text-muted-foreground">
                      Equipamentos virtuais requerem localização fixa. Clique no mapa ou insira as coordenadas.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Tipo de Localização</Label>
                  <RadioGroup
                    value={config.identification.location_type}
                    onValueChange={(v) => setConfig(prev => ({
                      ...prev,
                      identification: { ...prev.identification, location_type: v }
                    }))}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="gps" id="loc-gps" />
                      <Label htmlFor="loc-gps" className="flex items-center gap-2 cursor-pointer">
                        <Navigation className="w-4 h-4 text-blue-500" />
                        GPS Dinâmico
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="fixed" id="loc-fixed" />
                      <Label htmlFor="loc-fixed" className="flex items-center gap-2 cursor-pointer">
                        <MapPin className="w-4 h-4 text-red-500" />
                        Localização Fixa
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
                
                {config.identification.location_type === 'gps' && (
                  <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30 space-y-3">
                    <div className="flex items-center gap-3">
                      <Navigation className="w-8 h-8 text-blue-500" />
                      <div>
                        <p className="font-medium">GPS Dinâmico Selecionado</p>
                        <p className="text-sm text-muted-foreground">
                          A posição será atualizada automaticamente pelo dispositivo GPS conectado ao equipamento.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-2 border-t border-blue-500/20">
                      <Link2 className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-muted-foreground">Source SignalK:</span>
                      <code className="text-sm bg-blue-500/20 px-2 py-0.5 rounded font-mono text-blue-300">
                        navigation.position
                      </code>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {(isVirtualEquipment || config.identification.location_type === 'fixed') && (
              <div className="space-y-4">
                {/* Interactive Leaflet Map for location selection */}
                <div className="space-y-3">
                  <Label>Clique no Mapa para Selecionar</Label>
                  <div className="h-[280px] rounded-lg border overflow-hidden">
                    <MapContainer
                      center={[
                        config.identification.latitude || -26.9034,
                        config.identification.longitude || -48.6576
                      ]}
                      zoom={13}
                      className="h-full w-full"
                      style={{ background: '#1e293b' }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; OpenStreetMap'
                      />
                      <LocationSelector
                        position={
                          config.identification.latitude && config.identification.longitude
                            ? [config.identification.latitude, config.identification.longitude]
                            : null
                        }
                        onLocationSelect={(lat, lng) => {
                          setConfig(prev => ({
                            ...prev,
                            identification: {
                              ...prev.identification,
                              latitude: lat,
                              longitude: lng
                            }
                          }));
                          toast.success(`Localização definida: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
                        }}
                      />
                    </MapContainer>
                  </div>
                </div>
                
                {/* Manual coordinate input */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Latitude *</Label>
                    <Input
                      type="number"
                      step="any"
                      value={config.identification.latitude || ''}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        identification: { ...prev.identification, latitude: parseFloat(e.target.value) || null }
                      }))}
                      placeholder="-26.9034"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Longitude *</Label>
                    <Input
                      type="number"
                      step="any"
                      value={config.identification.longitude || ''}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        identification: { ...prev.identification, longitude: parseFloat(e.target.value) || null }
                      }))}
                      placeholder="-48.6576"
                    />
                  </div>
                </div>
                
                {config.identification.latitude && config.identification.longitude && (
                  <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30 flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-sm">
                      Localização definida: {config.identification.latitude.toFixed(6)}, {config.identification.longitude.toFixed(6)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
        
      case 'review':
        const selectedType = equipmentTypes.find(t => t.id === config.typeProfile.equipment_type_id);
        const TypeIcon = selectedType ? getEquipmentTypeIcon(selectedType) : Ship;
        
        return (
          <div className="space-y-4">
            <div className="text-center p-4 bg-primary/5 rounded-lg border border-primary/20">
              <TypeIcon className="w-12 h-12 mx-auto mb-2 text-primary" />
              <h3 className="text-xl font-bold">{config.identification.name}</h3>
              <p className="text-muted-foreground">{selectedType?.name || 'Tipo não definido'}</p>
              {config.identification.tag_senior && (
                <Badge className="mt-2">{config.identification.tag_senior}</Badge>
              )}
            </div>
            
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Resumo do Equipamento
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Nome:</span>
                  <span className="font-medium">{config.identification.name}</span>
                </div>
                {config.identification.tag_senior && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">TAG:</span>
                    <span className="font-medium">{config.identification.tag_senior}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tipo:</span>
                  <span className="font-medium">{selectedType?.name || '-'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Localização:</span>
                  <span className="font-medium">
                    {config.identification.location_type === 'gps' ? 'GPS Dinâmico' : 'Fixa'}
                  </span>
                </div>
                {config.identification.location_type === 'gps' && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Source GPS:</span>
                    <code className="font-mono text-xs bg-blue-500/20 px-1.5 py-0.5 rounded text-blue-300">
                      navigation.position
                    </code>
                  </div>
                )}
                {config.identification.location_type === 'fixed' && config.identification.latitude && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Coordenadas:</span>
                    <span className="font-mono text-xs">
                      {config.identification.latitude?.toFixed(4)}, {config.identification.longitude?.toFixed(4)}
                    </span>
                  </div>
                )}
                {config.identification.description && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Descrição:</span>
                    <span className="font-medium truncate max-w-[200px]">{config.identification.description}</span>
                  </div>
                )}
              </CardContent>
            </Card>
            
            <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
              <p className="text-sm text-center">
                <strong>Próximo passo:</strong> Após confirmar, você poderá configurar os sensores na página do equipamento.
              </p>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  // Handle close - reset state for new equipment
  const handleClose = useCallback(() => {
    // Reset state when closing (if not reprovisionamento)
    if (!existingEquipment) {
      resetWizardState();
    }
    onClose();
  }, [existingEquipment, onClose, resetWizardState]);

  // Determinar se o botão "Próximo" deve estar habilitado na etapa 0
  const canProceedFromZeroTouch = () => {
    if (currentStep !== 0) return true;
    // Reprovisionamento: mesmo gate do fluxo físico — device vinculado, opção
    // explícita de prosseguir sem conexão, ou "Pular" (que marca deviceConnected)
    if (existingEquipment) {
      return deviceConnected || proceedWithoutConnection;
    }

    // Equipamento físico: precisa ter dispositivo conectado OU usuário optou por prosseguir sem conexão
    if (!isVirtualEquipment) {
      return deviceConnected || proceedWithoutConnection;
    }
    
    // Equipamento virtual: precisa ter sido criado com sucesso
    return !!virtualCredentials;
  };

  // Handler para "Prosseguir mesmo assim"
  const handleProceedWithoutConnection = () => {
    setShowProceedWithoutConnection(false);
    setProceedWithoutConnection(true);
    // Avançar automaticamente para o próximo passo
    setCurrentStep(1);
  };

  // Mensagem de tooltip para o botão Próximo desabilitado
  const getNextButtonTooltip = () => {
    if (canProceedFromZeroTouch()) return '';
    if (!isVirtualEquipment) {
      if (!provisioningData) {
        return 'Gere o código de provisionamento primeiro';
      }
      return 'Aguarde o dispositivo conectar ou clique em "Pular (Configuração Manual)"';
    }
    return 'Crie o equipamento virtual primeiro';
  };

  const step = WIZARD_STEPS[currentStep];
  const StepIcon = step.icon;
  
  // Verificar se há progresso que seria perdido ao fechar
  const hasUnsavedProgress = () => {
    // Se gerou claim code ou tem dados de provisionamento, há progresso
    if (provisioningData) return true;
    // Se está em qualquer etapa além da 0
    if (currentStep > 0) return true;
    // Se é equipamento virtual e já criou
    if (virtualCredentials) return true;
    return false;
  };

  // Handler para tentar fechar o dialog
  const handleTryClose = () => {
    if (hasUnsavedProgress()) {
      setShowCancelConfirm(true);
    } else {
      handleClose();
    }
  };

  // Confirmar cancelamento
  const handleConfirmCancel = () => {
    setShowCancelConfirm(false);
    handleClose();
  };
  
  return (
    <>
      {/* AlertDialog para confirmar cancelamento */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar provisionamento?</AlertDialogTitle>
            <AlertDialogDescription>
              Todo o progresso será perdido e nenhum dado será salvo. 
              O equipamento não será criado no sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sim, cancelar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AlertDialog para prosseguir sem conexão */}
      <AlertDialog open={showProceedWithoutConnection} onOpenChange={setShowProceedWithoutConnection}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Prosseguir sem conexão?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  O Octopus ainda não detectou a conexão deste equipamento.
                </p>
                <p>
                  Se você prosseguir, o equipamento ficará pré-cadastrado no status 
                  <span className="font-semibold text-yellow-500"> "Aguardando Conexão Inicial"</span> e 
                  não terá acesso às funcionalidades completas até que a primeira conexão seja detectada.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Aguardar conexão</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleProceedWithoutConnection}
              className="bg-yellow-600 text-white hover:bg-yellow-700"
            >
              Prosseguir mesmo assim
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog 
        open={open} 
        onOpenChange={(newOpen) => {
          if (!newOpen) {
            handleTryClose();
          }
        }}
      >
      <DialogContent className="max-w-4xl w-[90vw] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {existingEquipment ? (
              <>
                <RefreshCw className="w-5 h-5" />
                Reprovisionar Equipamento
              </>
            ) : (
              <>
                <Settings2 className="w-5 h-5" />
                Provisionar Novo Equipamento
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {existingEquipment ? existingEquipment.name : 'Configure seu novo equipamento'}
          </DialogDescription>
        </DialogHeader>
        
        {/* Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Etapa {currentStep + 1} de {WIZARD_STEPS.length}
            </span>
            <span className="font-medium flex items-center gap-2">
              <StepIcon className="w-4 h-4" />
              {step.title}
            </span>
          </div>
          <Progress value={(currentStep + 1) / WIZARD_STEPS.length * 100} className="h-2" />
        </div>
        
        {/* Step indicator */}
        <div className="flex justify-center gap-1 py-2">
          {WIZARD_STEPS.map((s, idx) => {
            const Icon = s.icon;
            const isActive = idx === currentStep;
            const isCompleted = idx < currentStep;
            
            return (
              <div
                key={s.id}
                className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                  isActive ? 'bg-primary text-primary-foreground' :
                  isCompleted ? 'bg-green-500 text-white' :
                  'bg-muted text-muted-foreground'
                }`}
                title={s.title}
              >
                {isCompleted ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
            );
          })}
        </div>
        
        <Separator />
        
        {/* Content */}
        <div className="flex-1 overflow-auto py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            renderStepContent()
          )}
        </div>
        
        <DialogFooter className="flex justify-between border-t pt-4">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          
          {currentStep === WIZARD_STEPS.length - 1 ? (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {existingEquipment ? 'Reprovisionar' : 'Provisionar'}
                </>
              )}
            </Button>
          ) : (
            <Button 
              onClick={handleNext}
              disabled={!canProceedFromZeroTouch()}
              title={getNextButtonTooltip()}
            >
              Próximo
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ProvisioningWizard;
